import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { CalendarView } from '../components/CalendarView';
import { Textarea } from '../components/ui/Textarea';
import { DatePicker } from '../components/ui/DatePicker';
import { Tooltip } from '../components/ui/Tooltip';
import { Plus, Filter, Eye, Edit, Search, Bell, Calendar as CalendarIcon, List, Clock, CheckCircle2, X, Save, Trash2, AlertCircle } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
// Mock data
const initialDeadlinesData = Array.from({
  length: 50
}, (_, i) => {
  const types = ['Riunione annuale', 'Prova di emergenza', 'Tampone alimentare', 'Campionamento alimentare', 'Audit interno', 'Report annuale'];
  const priorities = ['high', 'medium', 'low'];
  const statuses = ['In scadenza', 'Urgente', 'Completata', 'Pianificata'];
  const today = new Date();
  const day = i % 28 + 1;
  const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return {
    id: String(i + 1),
    code: `SCA-2025/${String(i + 1).padStart(3, '0')}`,
    description: i % 3 === 0 ? `Rinnovo DVR ${i}` : `Corso Aggiornamento ${i}`,
    type: types[i % types.length],
    date: dateStr,
    client: `Azienda ${i} SRL`,
    notifyClient: i % 2 === 0 ? 'Sì' : 'No',
    status: statuses[i % statuses.length],
    priority: priorities[i % priorities.length] as 'high' | 'medium' | 'low',
    responsible: i % 4 === 0 ? 'Alessio' : i % 4 === 1 ? 'Lorenzo' : i % 4 === 2 ? 'Gian Franco' : 'Tiziano'
  };
});
const columnsConfig = [{
  key: 'code',
  label: 'Codice'
}, {
  key: 'description',
  label: 'Descrizione'
}, {
  key: 'type',
  label: 'Tipologia'
}, {
  key: 'client',
  label: 'Cliente'
}, {
  key: 'date',
  label: 'Data scadenza'
}, {
  key: 'notifyClient',
  label: 'Notifica'
}, {
  key: 'responsible',
  label: 'Responsabile'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function DeadlinesPage() {
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('calendar');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [recurrenceMode, setRecurrenceMode] = useState<'single' | 'multiple' | 'recurring'>('single');
  const [multipleDates, setMultipleDates] = useState<string[]>(['']);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const {
    addNotification
  } = useNotifications();
  // Custom Tipologia State
  const initialTipologiaOptions = [{
    value: 'Riunione annuale',
    label: 'Riunione annuale'
  }, {
    value: 'Prova di emergenza',
    label: 'Prova di emergenza'
  }, {
    value: 'Tampone alimentare',
    label: 'Tampone alimentare'
  }, {
    value: 'Campionamento alimentare',
    label: 'Campionamento alimentare'
  }, {
    value: 'Audit interno',
    label: 'Audit interno'
  }, {
    value: 'Report annuale',
    label: 'Report annuale'
  }];
  const [tipologiaOptions, setTipologiaOptions] = useState(initialTipologiaOptions);
  const [showCustomTipologiaInput, setShowCustomTipologiaInput] = useState(false);
  const [newTipologia, setNewTipologia] = useState('');
  const handleAddCustomTipologia = () => {
    if (newTipologia.trim()) {
      const newOption = {
        value: newTipologia.trim(),
        label: newTipologia.trim()
      };
      setTipologiaOptions([...tipologiaOptions, newOption]);
      setFormData({
        ...formData,
        type: newTipologia.trim()
      });
      setNewTipologia('');
      setShowCustomTipologiaInput(false);
    } else {
      setShowCustomTipologiaInput(false);
      setFormData({
        ...formData,
        type: ''
      });
    }
  };
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(initialDeadlinesData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['description', 'client', 'type', 'code']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleDelete = (item: any) => {
    setSelectedItem(item);
    setIsDeleteConfirmOpen(true);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setIsDeleteConfirmOpen(false);
      addNotification('success', 'Scadenza eliminata', 'La scadenza è stata eliminata con successo.');
    }, 1500);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    const nextCode = `SCA-2025/${String(initialDeadlinesData.length + 1).padStart(3, '0')}`;
    setFormData({
      code: nextCode,
      notifyClient: false,
      notifyRef: false
    });
    setRecurrenceMode('single');
    setMultipleDates(['']);
    setIsModalOpen(true);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
      addNotification('success', 'Scadenza salvata', 'La scadenza è stata salvata correttamente.');
    }, 2000);
  };
  const handleEventClick = (event: any) => {
    const item = initialDeadlinesData.find(d => d.id === event.id);
    if (item) {
      handleView(item);
    }
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  return <Layout data-id="element-3579">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-3580">
        <div data-id="element-3581">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-3582">Scadenziario</h1>
          <p className="text-slate-500 mt-1" data-id="element-3583">
            Gestione scadenze tecniche e normative.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-3584">
          <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-3585">
            <button onClick={() => setViewMode('calendar')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'calendar' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3586">
              <CalendarIcon className="h-4 w-4 inline-block mr-1.5" data-id="element-3587" />{' '}
              Calendario
            </button>
            <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3588">
              <List className="h-4 w-4 inline-block mr-1.5" data-id="element-3589" /> Lista
            </button>
          </div>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-3591" />} data-id="element-3590">
            Nuova scadenza
          </Button>
        </div>
      </div>

      {viewMode === 'list' ? <>
          <Card className="mb-6" noPadding data-id="element-3592">
            <div className="p-4 flex flex-col gap-4" data-id="element-3593">
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-3594">
                <div className="flex-1" data-id="element-3595">
                  <Input placeholder="Cerca scadenza..." startContent={<Search className="h-4 w-4" data-id="element-3597" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-3596" />
                </div>
                <div className="flex gap-2" data-id="element-3598">
                  <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-3600" />} data-id="element-3599">
                    Filtri{' '}
                    {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-3601">
                        {Object.keys(filters).filter(k => filters[k]).length}
                      </Badge>}
                  </Button>
                  <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="deadlines" data-id="element-3602" />
                </div>
              </div>

              {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-3603">
                  <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
              value: '',
              label: 'Tutte'
            }, ...tipologiaOptions]} data-id="element-3604" />
                  <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
              value: '',
              label: 'Tutti'
            }, {
              value: 'In scadenza',
              label: 'In scadenza'
            }, {
              value: 'Urgente',
              label: 'Urgente'
            }, {
              value: 'Completata',
              label: 'Completata'
            }, {
              value: 'Pianificata',
              label: 'Pianificata'
            }]} data-id="element-3605" />
                  <Select label="Responsabile" value={filters.responsible || ''} onChange={e => setFilter('responsible', e.target.value)} options={[{
              value: '',
              label: 'Tutti'
            }, {
              value: 'Alessio',
              label: 'Alessio'
            }, {
              value: 'Lorenzo',
              label: 'Lorenzo'
            }, {
              value: 'Gian Franco',
              label: 'Gian Franco'
            }, {
              value: 'Tiziano',
              label: 'Tiziano'
            }]} data-id="element-3606" />
                  <Select label="Notifica" value={filters.notifyClient || ''} onChange={e => setFilter('notifyClient', e.target.value)} options={[{
              value: '',
              label: 'Tutte'
            }, {
              value: 'Sì',
              label: 'Sì'
            }, {
              value: 'No',
              label: 'No'
            }]} data-id="element-3607" />
                  <div className="col-span-full flex justify-end" data-id="element-3608">
                    <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-3610" />} data-id="element-3609">
                      Resetta filtri
                    </Button>
                  </div>
                </div>}
            </div>
          </Card>

          <Card noPadding className="overflow-hidden" data-id="element-3611">
            <div className="overflow-x-auto" data-id="element-3612">
              <table className="w-full text-left border-collapse table-auto" data-id="element-3613">
                <thead data-id="element-3614">
                  <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-3615">
                    {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-4 py-4 ${col.key === 'actions' ? 'text-left pr-6' : ''}`} data-id="element-3616">
                          {col.label}
                        </th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-3617">
                  {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-3618">
                      {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-3619">
                          {item.code}
                        </td>}
                      {visibleColumns.includes('description') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-3620">
                          {item.description}
                        </td>}
                      {visibleColumns.includes('type') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3621">
                          {item.type}
                        </td>}
                      {visibleColumns.includes('client') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3622">
                          {item.client}
                        </td>}
                      {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm font-mono text-slate-600" data-id="element-3623">
                          {item.date}
                        </td>}
                      {visibleColumns.includes('notifyClient') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3624">
                          {item.notifyClient}
                        </td>}
                      {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3625">
                          {item.responsible}
                        </td>}
                      {visibleColumns.includes('status') && <td className="px-4 py-4 whitespace-nowrap" data-id="element-3626">
                          <Badge variant={item.status === 'Urgente' ? 'danger' : item.status === 'In scadenza' ? 'warning' : item.status === 'Completata' ? 'success' : 'info'} data-id="element-3627">
                            {item.status}
                          </Badge>
                        </td>}
                      {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-3628">
                          <div className="flex items-center gap-1" data-id="element-3629">
                            <Tooltip content="Visualizza scadenza" data-id="element-3630">
                              <Button variant="light" size="sm" className="!px-2" onClick={() => handleView(item)} data-id="element-3631">
                                <Eye className="h-4 w-4 text-slate-500" data-id="element-3632" />
                              </Button>
                            </Tooltip>
                            <Tooltip content="Modifica scadenza" data-id="element-3633">
                              <Button variant="light" size="sm" className="!px-2" onClick={() => handleEdit(item)} data-id="element-3634">
                                <Edit className="h-4 w-4 text-seform-orange" data-id="element-3635" />
                              </Button>
                            </Tooltip>
                            <Tooltip content="Elimina scadenza" data-id="element-3636">
                              <Button variant="light" size="sm" className="!px-2" onClick={() => handleDelete(item)} data-id="element-3637">
                                <Trash2 className="h-4 w-4 text-red-500" data-id="element-3638" />
                              </Button>
                            </Tooltip>
                          </div>
                        </td>}
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="border-t border-slate-100" data-id="element-3639">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-3640" />
            </div>
          </Card>
        </> : <CalendarView events={searchResult.map(item => ({
      id: item.id,
      title: item.description,
      date: item.date,
      type: item.type,
      priority: item.priority,
      client: item.client
    }))} onEventClick={handleEventClick} data-id="element-3641" />}

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Dettaglio scadenza" size="lg" data-id="element-3642">
        {selectedItem && <div className="space-y-6" data-id="element-3643">
            <div className="grid grid-cols-2 gap-4" data-id="element-3644">
              <div data-id="element-3645">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3646">
                  Codice
                </span>
                <p className="font-mono font-medium" data-id="element-3647">{selectedItem.code}</p>
              </div>
              <div data-id="element-3648">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3649">
                  Stato
                </span>
                <div className="mt-1" data-id="element-3650">
                  <Badge variant={selectedItem.status === 'Urgente' ? 'danger' : selectedItem.status === 'In scadenza' ? 'warning' : selectedItem.status === 'Completata' ? 'success' : 'info'} data-id="element-3651">
                    {selectedItem.status}
                  </Badge>
                </div>
              </div>
              <div className="col-span-full" data-id="element-3652">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3653">
                  Descrizione
                </span>
                <p className="font-medium" data-id="element-3654">{selectedItem.description}</p>
              </div>
              <div data-id="element-3655">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3656">
                  Tipologia
                </span>
                <p className="font-medium" data-id="element-3657">{selectedItem.type}</p>
              </div>
              <div data-id="element-3658">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3659">
                  Cliente
                </span>
                <p className="font-medium" data-id="element-3660">{selectedItem.client}</p>
              </div>
              <div data-id="element-3661">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3662">
                  Data scadenza
                </span>
                <p className="font-mono font-medium" data-id="element-3663">{selectedItem.date}</p>
              </div>
              <div data-id="element-3664">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3665">
                  Responsabile
                </span>
                <p className="font-medium" data-id="element-3666">{selectedItem.responsible}</p>
              </div>
              <div data-id="element-3667">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3668">
                  Notifica cliente
                </span>
                <p className="font-medium" data-id="element-3669">{selectedItem.notifyClient}</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3670">
              <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-3671">
                Chiudi
              </Button>
              <Button onClick={() => {
            setIsViewModalOpen(false);
            handleEdit(selectedItem);
          }} startContent={<Edit className="h-4 w-4" data-id="element-3673" />} data-id="element-3672">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica scadenza' : 'Nuova scadenza'} size="lg" data-id="element-3674">
        <AnimatePresence mode="wait" data-id="element-3675">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-16" data-id="element-3676">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-3677">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-3678" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-3679">
                Scadenza salvata con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-3680">
              <div className="space-y-6" data-id="element-3681">
                <section data-id="element-3682">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3683">
                    <Clock className="h-4 w-4" data-id="element-3684" /> Dati base
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3685">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} data-id="element-3686" />
                    <div data-id="element-3687">
                      <Select label="Tipologia" value={formData.type || ''} onChange={e => {
                    if (e.target.value === '__custom__') {
                      setShowCustomTipologiaInput(true);
                    } else {
                      setFormData({
                        ...formData,
                        type: e.target.value
                      });
                      setShowCustomTipologiaInput(false);
                    }
                  }} options={[{
                    value: '',
                    label: 'Seleziona...'
                  }, ...tipologiaOptions, {
                    value: '__custom__',
                    label: '+ Aggiungi nuova tipologia...'
                  }]} required data-id="element-3688" />
                      {showCustomTipologiaInput && <div className="mt-2 animate-in fade-in slide-in-from-top-2" data-id="element-3689">
                          <Input autoFocus placeholder="Inserisci nuova tipologia" value={newTipologia} onChange={e => setNewTipologia(e.target.value)} onBlur={handleAddCustomTipologia} onKeyDown={e => e.key === 'Enter' && handleAddCustomTipologia()} data-id="element-3690" />
                        </div>}
                    </div>
                    <div className="col-span-full" data-id="element-3691">
                      <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} required data-id="element-3692" />
                    </div>
                    <Select label="Cliente" value={formData.client || ''} onChange={e => setFormData({
                  ...formData,
                  client: e.target.value
                })} options={[{
                  value: 'Azienda 0 SRL',
                  label: 'Azienda 0 SRL'
                }, {
                  value: 'Azienda 1 SRL',
                  label: 'Azienda 1 SRL'
                }, {
                  value: 'Azienda 2 SRL',
                  label: 'Azienda 2 SRL'
                }]} required data-id="element-3693" />
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-3694" />
                  </div>
                </section>

                <section data-id="element-3695">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3696">
                    <CalendarIcon className="h-4 w-4" data-id="element-3697" /> Date e ripetizioni
                  </h4>
                  <div className="flex gap-4 mb-4" data-id="element-3698">
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-id="element-3699">
                      <input type="radio" name="recurrence" checked={recurrenceMode === 'single'} onChange={() => setRecurrenceMode('single')} className="text-seform-orange focus:ring-seform-orange" data-id="element-3700" />
                      Data singola
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-id="element-3701">
                      <input type="radio" name="recurrence" checked={recurrenceMode === 'multiple'} onChange={() => setRecurrenceMode('multiple')} className="text-seform-orange focus:ring-seform-orange" data-id="element-3702" />
                      Date multiple
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-id="element-3703">
                      <input type="radio" name="recurrence" checked={recurrenceMode === 'recurring'} onChange={() => setRecurrenceMode('recurring')} className="text-seform-orange focus:ring-seform-orange" data-id="element-3704" />
                      Periodo ripetuto
                    </label>
                  </div>

                  {recurrenceMode === 'single' && <DatePicker label="Data scadenza" value={formData.date || ''} onChange={e => setFormData({
                ...formData,
                date: e.target.value
              })} data-id="element-3705" />}

                  {recurrenceMode === 'multiple' && <div className="space-y-3" data-id="element-3706">
                      <p className="text-xs text-slate-500" data-id="element-3707">
                        Aggiungi più date per questa scadenza
                      </p>
                      {multipleDates.map((date, index) => <div key={index} className="flex gap-2 items-end" data-id="element-3708">
                          <div className="flex-1" data-id="element-3709">
                            <DatePicker label={index === 0 ? 'Data scadenza' : `Data scadenza ${index + 1}`} value={date} onChange={e => {
                      const updated = [...multipleDates];
                      updated[index] = e.target.value;
                      setMultipleDates(updated);
                    }} data-id="element-3710" />
                          </div>
                          {index > 0 && <Button size="sm" variant="flat" color="danger" className="mb-0.5" onClick={() => {
                    setMultipleDates(multipleDates.filter((_, i) => i !== index));
                  }} data-id="element-3711">
                              <X className="h-4 w-4" data-id="element-3712" />
                            </Button>}
                        </div>)}
                      <Button size="sm" variant="flat" startContent={<Plus className="h-4 w-4" data-id="element-3714" />} onClick={() => setMultipleDates([...multipleDates, ''])} data-id="element-3713">
                        Aggiungi data
                      </Button>
                    </div>}

                  {recurrenceMode === 'recurring' && <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-3715">
                      <DatePicker label="Data inizio" data-id="element-3716" />
                      <div className="flex gap-2 items-end" data-id="element-3717">
                        <Input label="Ripeti ogni" type="number" defaultValue="1" data-id="element-3718" />
                        <Select options={[{
                    value: 'M',
                    label: 'Mesi'
                  }, {
                    value: 'Y',
                    label: 'Anni'
                  }]} defaultValue="Y" data-id="element-3719" />
                      </div>
                      <Input label="Per (volte)" type="number" defaultValue="5" data-id="element-3720" />
                      <div className="col-span-full pt-2" data-id="element-3721">
                        <p className="text-xs font-medium text-slate-600 mb-1" data-id="element-3722">
                          Anteprima occorrenze:
                        </p>
                        <div className="flex gap-2 flex-wrap" data-id="element-3723">
                          <Badge variant="neutral" data-id="element-3724">10/05/2024</Badge>
                          <Badge variant="neutral" data-id="element-3725">10/05/2025</Badge>
                          <Badge variant="neutral" data-id="element-3726">10/05/2026</Badge>
                        </div>
                      </div>
                    </div>}
                </section>

                <section data-id="element-3727">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3728">
                    <Bell className="h-4 w-4" data-id="element-3729" /> Notifiche
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3730">
                    <div className="flex items-center gap-2" data-id="element-3731">
                      <input type="checkbox" id="notifyClient" className="rounded text-seform-orange focus:ring-seform-orange" checked={formData.notifyClient || false} onChange={e => setFormData({
                    ...formData,
                    notifyClient: e.target.checked
                  })} data-id="element-3732" />
                      <label htmlFor="notifyClient" className="text-sm text-slate-700" data-id="element-3733">
                        Invia notifica al cliente
                      </label>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-3734">
                      <input type="checkbox" id="notifyRef" className="rounded text-seform-orange focus:ring-seform-orange" checked={formData.notifyRef || false} onChange={e => setFormData({
                    ...formData,
                    notifyRef: e.target.checked
                  })} data-id="element-3735" />
                      <label htmlFor="notifyRef" className="text-sm text-slate-700" data-id="element-3736">
                        Invia notifica referente SEF
                      </label>
                    </div>
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3737">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-3738">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-3740" />} data-id="element-3739">
                    Salva scadenza
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Conferma eliminazione" size="sm" data-id="element-3741">
        <AnimatePresence mode="wait" data-id="element-3742">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-3743">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-3744">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-3745" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-3746">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-3747">
              <div className="space-y-6" data-id="element-3748">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-3749">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-3750" />
                  <p className="text-sm text-red-700" data-id="element-3751">
                    Attenzione: questa azione è irreversibile. La scadenza verrà
                    eliminata definitivamente.
                  </p>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-3752">
                  <Button variant="flat" color="default" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-3753">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} data-id="element-3754">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}